import { useCallback, useRef } from 'react'
import { query, where } from 'firebase/firestore'
import { writeEventInterest } from '../lib/interestWrites'
import { eventInterestsCol } from '../lib/converters'
import { useFirestoreCollection } from './useFirestore'
import { runMutation } from '../lib/firestoreMutation'
import { track } from '../lib/analytics'
import { announceRsvp } from '../lib/rsvpBridge'
import { attendanceStatus, type EventAttendanceStatus, type EventInterest } from '../types'
import { fbt } from 'fbtee'

export type EventInterestStatus = 'none' | EventAttendanceStatus

export function useEventInterest(userId: string | null, eventId: string) {
  const ref = userId
    ? query(
        eventInterestsCol(),
        where('userId', '==', userId),
        where('eventId', '==', eventId),
      )
    : null
  const { data, loading, error } = useFirestoreCollection<EventInterest>(ref)

  const writingRef = useRef(false)
  const docId = userId ? (data[0]?.id ?? null) : null
  const stored = data[0]?.status
  const status: EventInterestStatus = docId ? attendanceStatus(stored) : 'none'

  const ensureInterested = useCallback(async () => {
    if (!userId || status !== 'none' || writingRef.current) return
    writingRef.current = true
    try {
      await runMutation(fbt("Couldn't mark event interest", 'Error toast when marking interest in an event fails'), () =>
        writeEventInterest(userId, eventId, null, 'interested'),
      )
    } finally {
      writingRef.current = false
    }
  }, [userId, eventId, status])

  const ensureGoing = useCallback(async () => {
    if (!userId || status === 'going' || writingRef.current) return
    writingRef.current = true
    try {
      await runMutation(fbt("Couldn't mark event interest", 'Error toast when marking interest in an event fails'), async () => {
        await writeEventInterest(userId, eventId, docId, 'going')
        announceRsvp({ eventId, status: 'going' })
      })
    } finally {
      writingRef.current = false
    }
  }, [userId, eventId, docId, status])

  const setStatus = useCallback(
    async (next: EventInterestStatus) => {
      if (!userId || next === status) return
      await runMutation(fbt("Couldn't update event interest", 'Error toast when changing an event attendance status fails'), async () => {
        if (status === 'none') {
          if (writingRef.current) return
          writingRef.current = true
          try {
            await writeEventInterest(userId, eventId, null, next)
          } finally {
            writingRef.current = false
          }
        } else if (docId) {
          await writeEventInterest(userId, eventId, docId, next)
        }
        track('event_interest_toggled', { status: next, event_id: eventId })
        // Only once the write landed: the confirmation doubles as the prompt to
        // link a group, and there's nothing to plan around a failed RSVP.
        // `ensureInterested` stays silent — it's a side effect of hearting an
        // artist, not an answer the user gave.
        if (next === 'going' || next === 'interested') announceRsvp({ eventId, status: next })
      })
    },
    [userId, eventId, docId, status],
  )

  return { status, loading, error, setStatus, ensureInterested, ensureGoing }
}
